import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, AlertCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";

export default function SignupPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const cancelled = searchParams.get("cancelled") === "true";

  const [step, setStep] = useState(1); // 1=invite, 2=details, 3=checkout
  const [inviteCode, setInviteCode] = useState("");
  const [inviteValid, setInviteValid] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const validateInvite = async () => {
    setError("");
    setLoading(true);
    try {
      await api.validateInvite(inviteCode);
      setInviteValid(true);
      setStep(2);
    } catch (e) {
      setError(e.response?.data?.detail || "Invalid invite code");
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async () => {
    setError("");
    if (!name.trim() || !phone.trim()) {
      setError("Name and phone number are required");
      return;
    }
    setLoading(true);
    try {
      const res = await api.signup({
        name: name.trim(),
        real_number: phone.trim(),
        email: email.trim(),
        invite_code: inviteCode,
        origin_url: window.location.origin,
      });
      // Redirect to Stripe checkout
      window.location.href = res.checkout_url;
    } catch (e) {
      setError(e.response?.data?.detail || "Signup failed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] text-white flex flex-col">
      {/* Top bar */}
      <header className="border-b border-[#1f1f23]">
        <div className="max-w-lg mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors text-sm">
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
            <span className="font-heading text-lg tracking-tight">PROX</span>
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-start justify-center pt-16 px-6">
        <div className="w-full max-w-md">
          <h1 className="font-heading text-3xl tracking-tight mb-2" data-testid="signup-title">
            Get your PROX number
          </h1>
          <p className="font-mono text-xs text-zinc-500 mb-8 uppercase tracking-wide">
            $7.99/mo · Private proxy number · Cancel anytime
          </p>

          {cancelled && (
            <div className="bg-[#1a1a1c] border border-amber-500/30 text-amber-400 text-sm p-3 mb-6 font-mono">
              Payment was cancelled. You can try again below.
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 bg-[#1a1a1c] border border-red-500/30 text-red-400 text-sm p-3 mb-6" data-testid="signup-error">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Step 1: Invite Code */}
          {step === 1 && (
            <div data-testid="signup-step-invite">
              <label className="font-mono text-xs text-zinc-500 uppercase tracking-wider block mb-2">
                Invite Code
              </label>
              <input
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                placeholder="PROX-XXXXXXXX"
                className="w-full bg-[#0e0e10] border border-[#27272a] text-white font-mono text-sm px-4 py-3 focus:outline-none focus:border-zinc-500 placeholder:text-zinc-600"
                data-testid="invite-code-input"
                onKeyDown={(e) => e.key === "Enter" && inviteCode && validateInvite()}
              />
              <button
                onClick={validateInvite}
                disabled={!inviteCode || loading}
                className="mt-4 w-full bg-white text-black font-mono text-sm px-4 py-3 hover:bg-zinc-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="validate-invite-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                Continue
              </button>
              <p className="font-mono text-[10px] text-zinc-600 mt-4">
                PROX is in invite-only beta. Get a code from an existing member.
              </p>
            </div>
          )}

          {/* Step 2: Details */}
          {step === 2 && (
            <div data-testid="signup-step-details">
              <div className="flex items-center gap-2 text-emerald-400 text-sm mb-6">
                <Check className="w-4 h-4" />
                <span className="font-mono text-xs">Invite code verified</span>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="font-mono text-xs text-zinc-500 uppercase tracking-wider block mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jane Doe"
                    className="w-full bg-[#0e0e10] border border-[#27272a] text-white text-sm px-4 py-3 focus:outline-none focus:border-zinc-500 placeholder:text-zinc-600"
                    data-testid="signup-name-input"
                  />
                </div>
                <div>
                  <label className="font-mono text-xs text-zinc-500 uppercase tracking-wider block mb-2">
                    Phone Number (your real number)
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+1 555 123 4567"
                    className="w-full bg-[#0e0e10] border border-[#27272a] text-white font-mono text-sm px-4 py-3 focus:outline-none focus:border-zinc-500 placeholder:text-zinc-600"
                    data-testid="signup-phone-input"
                  />
                </div>
                <div>
                  <label className="font-mono text-xs text-zinc-500 uppercase tracking-wider block mb-2">
                    Email (optional)
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    className="w-full bg-[#0e0e10] border border-[#27272a] text-white text-sm px-4 py-3 focus:outline-none focus:border-zinc-500 placeholder:text-zinc-600"
                    data-testid="signup-email-input"
                  />
                </div>
              </div>

              <button
                onClick={handleSignup}
                disabled={!name || !phone || loading}
                className="mt-6 w-full bg-white text-black font-mono text-sm px-4 py-3 hover:bg-zinc-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="signup-checkout-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                Continue to Payment — $7.99/mo
              </button>

              <button
                onClick={() => { setStep(1); setInviteValid(false); setError(""); }}
                className="mt-3 w-full text-zinc-500 hover:text-white font-mono text-xs py-2 transition-colors"
              >
                Use a different invite code
              </button>
            </div>
          )}
        </div>
      </main>

      <footer className="border-t border-[#1f1f23] mt-auto">
        <div className="max-w-lg mx-auto px-6 py-4 flex gap-6 text-xs text-zinc-600">
          <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
          <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
          <Link to="/sms-terms" className="hover:text-white transition-colors">SMS Terms</Link>
        </div>
      </footer>
    </div>
  );
}
